function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import deepEqual from 'fast-deep-equal';
import { nanoid } from 'nanoid';
import isMatch from 'lodash.ismatch';
import Log from '@deephaven/log';
import { isComponent, isRoot, isStack, GoldenLayoutThemeExport } from '@deephaven/golden-layout';
import { assertNotNull } from '@deephaven/utils';
var log = Log.module('LayoutUtils');
function isComponentConfig(config) {
  return config.componentName !== undefined;
}
export function isReactComponentConfig(config) {
  var reactConfig = config;
  // Golden layout sets the type to 'component' and componentName to 'lm-react-component' in `createContentItem`, then changes it back in `toConfig`
  // For our purposes, we need to check both.
  return (isComponentConfig(config) && config.componentName === 'lm-react-component' || config.type === 'react-component') && reactConfig.component !== undefined;
}
function isHTMLElement(element) {
  return element.focus !== undefined;
}
function isStackItemConfig(config) {
  return config.type === 'stack';
}
class LayoutUtils {
  static activateTab(root, config) {
    var stack = LayoutUtils.getStackForRoot(root, config, false);
    if (!stack) {
      log.error('Could not find stack for config', config);
      return;
    }
    // Find the tab with the specified table and activate it
    var contentItem = LayoutUtils.getContentItemInStack(stack, config);
    if (contentItem) {
      stack.setActiveContentItem(contentItem);
    }
  }

  /**
   * Is the tab with the given config active
   * @param root A GoldenLayout content item with the tab
   * @param config Tab config to match
   * @returns True if the tab is active
   */
  static isActiveTab(root, config) {
    var stack = LayoutUtils.getStackForRoot(root, config, false);
    if (!stack) {
      log.error('Could not find stack for config', config);
      return false;
    }
    // Find the item with the specified config and compare with active item
    var contentItem = LayoutUtils.getContentItemInStack(stack, config);
    var activeItem = stack.getActiveContentItem();
    return activeItem === contentItem;
  }

  /**
   * Adds a stack to the root layout specified. Adds to the first row/column with only one item
   * @param parent A GoldenLayout content item to add the stack to
   * @returns The newly created stack.
   */
  static addStack(parent) {
    var columnPreferred = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    if (isRoot(parent)) {
      var rowOrColConfig = {
        type: columnPreferred ? 'column' : 'row'
      };
      if (parent.contentItems == null || parent.contentItems.length === 0) {
        parent.addChild(rowOrColConfig);
      }
      var child = parent.contentItems[0];
      var _isCorrectType = columnPreferred ? child.isColumn : child.isRow;
      if (!_isCorrectType) {
        parent.removeChild(child, true);
        parent.addChild(rowOrColConfig);

        // The addChild may cause the element that has focus to be removed from the DOM, which changes focus to the body
        // Try and maintain the focus as best we can. The unfocused element may still send a blur/focus event so that needs to be handled correctly.
        var maintainFocusElement = document.activeElement;
        parent.contentItems[0].addChild(child);
        if (maintainFocusElement && maintainFocusElement.focus != null) {
          maintainFocusElement.focus();
        }
      }
      return this.addStack(parent.contentItems[0], columnPreferred);
    }
    if (parent.contentItems.length < 2) {
      parent.addChild({
        type: 'stack'
      });
      return parent.contentItems[parent.contentItems.length - 1];
    }
    var newParent = parent.contentItems[parent.contentItems.length - 1];
    var isCorrectType = !columnPreferred ? newParent.isColumn : newParent.isRow;

    // This is usually triggered because we hit a stack within the last row/column
    if (!isCorrectType) {
      var _newParent$config, _newParent$config2;
      var inverseRowOrColConfig = parent.layoutManager.createContentItem({
        type: !columnPreferred ? 'column' : 'row',
        height: (_newParent$config = newParent.config) === null || _newParent$config === void 0 ? void 0 : _newParent$config.height,
        width: (_newParent$config2 = newParent.config) === null || _newParent$config2 === void 0 ? void 0 : _newParent$config2.width
      }, parent);
      parent.replaceChild(newParent, inverseRowOrColConfig);
      parent.contentItems[parent.contentItems.length - 1].addChild(newParent);
      newParent = parent.contentItems[parent.contentItems.length - 1];
    }
    return this.addStack(newParent, !columnPreferred);
  }

  /**
   * Gets all stack containers in the layout
   * @param layout GoldenLayout instance
   * @returns The found stack containers
   */
  static getAllStackContainers(layout) {
    // eslint-disable-next-line no-underscore-dangle
    return layout._findAllStackContainers();
  }

  /**
   * Get the index of the stack that is currently focused
   * @param allStacks All the stacks
   * @returns The focused stack's index or -1 if not found
   */
  static getFocusedStackIndex(allStacks) {
    // NOTE: We target the 'lm_focusin' class because GoldenLayout automatically applies this class
    // to tab elements when they receive focus. Until we enhance focus tracking in GoldenLayout, we
    // will have to rely on this internal CSS class.
    return allStacks.findIndex(stack => stack.header.tabs.some(tab => tab.element[0].classList.contains('lm_focusin')));
  }

  /**
   * Get the stack that is currently focused
   * @param layout GoldenLayout instance
   * @returns The focused stack or undefined if none found
   */
  static getFocusedStack(layout) {
    var allStacks = LayoutUtils.getAllStackContainers(layout);
    var focusedStackIndex = LayoutUtils.getFocusedStackIndex(allStacks);
    return allStacks[focusedStackIndex];
  }

  /**
   * Gets a stack by its ID
   * @param item Golden layout content item to search for the stack
   * @param searchId the ID
   */
  static getStackById(item, searchId) {
    var _item$config;
    var allowEmptyStack = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (allowEmptyStack && isStack(item) && item.contentItems.length === 0) {
      return item;
    }
    if (searchId === ((_item$config = item.config) === null || _item$config === void 0 ? void 0 : _item$config.id)) {
      if (isStack(item)) {
        return item;
      }
      throw new Error("Item with ID ".concat(searchId, " is not a stack"));
    }
    if (item.contentItems == null) {
      return null;
    }
    for (var i = 0; i < item.contentItems.length; i += 1) {
      var stack = this.getStackById(item.contentItems[i], searchId, allowEmptyStack);
      if (stack) {
        return stack;
      }
    }
    return null;
  }

  /**
   * Gets a content item by its ID
   * @param item Golden layout content item to search for the content item. Typically the root.
   * @param searchId the ID
   */
  static getContentItemById(item, searchId) {
    var _item$config2;
    if (((_item$config2 = item.config) === null || _item$config2 === void 0 ? void 0 : _item$config2.id) === searchId) {
      return item;
    }
    if (item.contentItems == null) {
      return null;
    }
    for (var i = 0; i < item.contentItems.length; i += 1) {
      var contentItem = this.getContentItemById(item.contentItems[i], searchId);
      if (contentItem) {
        return contentItem;
      }
    }
    return null;
  }

  /**
   * Gets the first stack which contains a contentItem with the given config values
   * @param item Golden layout content item to search for the stack
   * @param config The item properties to match
   */
  static getStackForConfig(item) {
    var config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var allowEmptyStack = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (allowEmptyStack && isStack(item) && item.contentItems.length === 0) {
      return item;
    }
    if (item.contentItems == null) {
      return null;
    }
    for (var i = 0; i < item.contentItems.length; i += 1) {
      var contentItem = item.contentItems[i];
      if (contentItem.isComponent && contentItem.config != null) {
        if (isMatch(contentItem.config, config)) {
          return item;
        }
      }
      var stack = this.getStackForConfig(contentItem, config, allowEmptyStack);
      if (stack) {
        return stack;
      }
    }
    return null;
  }

  /**
   * Gets a stack matching the specified config
   * @param root The root GoldenLayout element
   * @param config The item config type to match, eg. { component: 'IrisGridPanel', title: 'Table Name' }
   * @param createIfNecessary Whether to create the stack if it does not exist.
   * @param matchComponentType If the config doesn't match exactly, just find another one of the same component type
   * @param allowEmptyStack If no configs match, search for an empty stack that can be used
   */
  static getStackForRoot(root, config) {
    var createIfNecessary = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var matchComponentType = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
    var allowEmptyStack = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
    var stack = this.getStackForConfig(root, config);
    if (!stack && matchComponentType) {
      stack = this.getStackForConfig(root, {
        component: config.component
      }, allowEmptyStack);
    }
    if (!stack && createIfNecessary) {
      stack = this.addStack(root);
    }
    return stack;
  }

  /**
   * Gets a stack matching one of the specified types, creates new stack if necessary
   * @param root The GoldenLayout root to find or create the stack in
   * @param types The array of component types to match
   * @param createIfNecessary Whether to create the stack if it does not exist
   * @param matchComponentType If the config doesn't match exactly, just find another one of the same component type
   * @param allowEmptyStack If no configs match, search for an empty stack that can be used
   */
  static getStackForComponentTypes(root, types) {
    var createIfNecessary = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var matchComponentType = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
    var allowEmptyStack = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
    for (var i = 0; i < types.length; i += 1) {
      var component = types[i];
      var isLastType = i === types.length - 1;
      var stack = LayoutUtils.getStackForRoot(root, {
        component
      }, createIfNecessary && isLastType, matchComponentType, allowEmptyStack);
      if (stack) {
        return stack;
      }
    }
    return null;
  }

  /**
   * Gets first content item with the specified config in stack.
   * @param stack The stack to search for the item
   * @param config The item config type to match, eg. { component: 'IrisGridPanel', title: 'Table Name' }
   * @returns Returns the found content item, null if not found.
   */
  static getContentItemInStack(stack, config) {
    if (!stack) {
      return null;
    }
    for (var i = 0; i < stack.contentItems.length; i += 1) {
      var contentItem = stack.contentItems[i];
      if (contentItem.isComponent && contentItem.config != null) {
        if (isMatch(contentItem.config, config)) {
          return contentItem;
        }
      }
    }
    return null;
  }

  /**
   * Removes dynamic props from components in the given config so this config could be serialized
   * @param config Config objec
   * @returns Dehydrated config
   */
  static dehydrateLayoutConfig(config, dehydrateComponent) {
    if (config == null || !config.length) {
      return [];
    }
    var dehydratedConfig = [];
    for (var i = 0; i < config.length; i += 1) {
      var itemConfig = config[i];
      var content = itemConfig.content;
      if (isReactComponentConfig(itemConfig)) {
        var dehydratedComponent = dehydrateComponent(itemConfig.component, itemConfig);
        if (dehydratedComponent != null) {
          dehydratedConfig.push(dehydratedComponent);
        } else {
          log.debug2("dehydrateLayoutConfig: skipping unmapped component \"".concat(itemConfig.component, "\""));
        }
      } else if (content) {
        var layoutItemConfig = _objectSpread(_objectSpread({}, itemConfig), {}, {
          content: LayoutUtils.dehydrateLayoutConfig(content, dehydrateComponent)
        });
        dehydratedConfig.push(layoutItemConfig);
      } else {
        dehydratedConfig.push(itemConfig);
      }
    }
    return dehydratedConfig;
  }
  static getTabPoint(glContainer) {
    var tab = glContainer.tab;
    if (tab == null) {
      throw new Error("Cannot get tab for panel container ".concat(glContainer));
    }
    var tabRect = tab.element[0].getBoundingClientRect();
    return [tabRect.left + tabRect.width * 0.5, tabRect.bottom - 8];
  }

  /**
   * Drop minor changes in Layout Configuration for deep comparison
   * @param config Layout Configuration
   *
   * minor changes:
   * 1. sorts in grid
   * 2. quick filters in grid
   * 3. active item
   * 4. isStuckToBottom/isStuckToRight
   *
   * item id is also removed
   */
  static dropLayoutMinorChange(config) {
    for (var i = 0; i < config.length; i += 1) {
      var itemConfig = config[i];
      var content = itemConfig.content;
      if (content !== undefined) {
        if (isStackItemConfig(itemConfig)) {
          delete itemConfig.activeItemIndex;
        }
        LayoutUtils.dropLayoutMinorChange(content);
      } else if (isReactComponentConfig(itemConfig) && itemConfig.component === 'IrisGridPanel') {
        if (itemConfig.props.panelState != null) {
          delete itemConfig.id;
          itemConfig.props.panelState.irisGridState.sorts = [];
          itemConfig.props.panelState.irisGridState.quickFilters = [];
          itemConfig.props.panelState.gridState.isStuckToBottom = false;
          itemConfig.props.panelState.gridState.isStuckToRight = false;
        }
      }
    }
  }

  /**
   * Compare two layouts to see if they are equivalent
   * @param layout1 A Golden Layout config object
   * @param layout2 Another Golden layout config object
   * @param major When true, will ignore "minor" property differences (eg. sorts)
   */
  static isEqual(layout1, layout2) {
    var major = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var layout1Clone = LayoutUtils.cloneLayout(layout1);
    var layout2Clone = LayoutUtils.cloneLayout(layout2);
    if (major) {
      LayoutUtils.dropLayoutMinorChange(layout1Clone);
      LayoutUtils.dropLayoutMinorChange(layout2Clone);
      return deepEqual(layout1Clone, layout2Clone);
    }
    // Pass cloned layouts to avoid false negatives
    // when comparing layouts with undefined and missing properties
    return deepEqual(layout1Clone, layout2Clone);
  }
  static cloneLayout(layout) {
    return JSON.parse(JSON.stringify(layout));
  }

  /**
   * Adds dynamic props to components in the given config so this config could be used to initialize a layout
   * @param config Dehydrated config object
   * @param hydrateComponent Function to hydrate the component
   * @returns Hydrated config
   */
  static hydrateLayoutConfig(config, hydrateComponent) {
    if (config == null || !config.length) {
      return [];
    }
    var hydratedConfig = [];
    for (var i = 0; i < config.length; i += 1) {
      var itemConfig = config[i];
      if (isReactComponentConfig(itemConfig)) {
        var _itemConfig$id;
        var component = itemConfig.component,
          _itemConfig$props = itemConfig.props,
          props = _itemConfig$props === void 0 ? {} : _itemConfig$props;
        hydratedConfig.push(_objectSpread(_objectSpread({}, itemConfig), {}, {
          id: (_itemConfig$id = itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.id) !== null && _itemConfig$id !== void 0 ? _itemConfig$id : nanoid(),
          props: hydrateComponent(component, props)
        }));
      } else if (itemConfig.content !== undefined) {
        var contentConfig = LayoutUtils.hydrateLayoutConfig(itemConfig.content, hydrateComponent);
        if (isStackItemConfig(itemConfig) && itemConfig.activeItemIndex != null && itemConfig.activeItemIndex >= contentConfig.length) {
          log.warn('Fixing bad activeItemIndex!', itemConfig.activeItemIndex, itemConfig);
          itemConfig.activeItemIndex = 0;
        }
        hydratedConfig.push(_objectSpread(_objectSpread({}, itemConfig), {}, {
          content: contentConfig
        }));
      } else {
        hydratedConfig.push(itemConfig);
      }
    }
    return hydratedConfig;
  }

  /**
   * Opens a component. It will try and open the component in an existing stack of the same component.
   * If `replaceExisting` is true and there is a component found with the same `config.id`, it will replace that component with this one.
   * If `allowStack` is true and there is a component of the same type found, it will open in that stack (potentially covering up a panel).
   * @param root The GoldenLayout root to open the component in
   * @param config The component config definition to open
   * @param replaceExisting Whether it should replace the existing one matching component type and id, or open a new one
   * @param replaceConfig The component config to replace
   * @param createNewStack True to force opening in a new stack, false to try and open in a stack with the same type of component.
   * @param focusElement The element to focus on
   * @param dragEvent Whether component is being created with a drag, mouse event is initial position for drag proxy
   */
  static openComponent() {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      root = _ref.root,
      configParam = _ref.config,
      _ref$stack = _ref.stack,
      stackParam = _ref$stack === void 0 ? undefined : _ref$stack,
      _ref$replaceExisting = _ref.replaceExisting,
      replaceExisting = _ref$replaceExisting === void 0 ? true : _ref$replaceExisting,
      _ref$replaceConfig = _ref.replaceConfig,
      replaceConfig = _ref$replaceConfig === void 0 ? undefined : _ref$replaceConfig,
      _ref$createNewStack = _ref.createNewStack,
      createNewStack = _ref$createNewStack === void 0 ? false : _ref$createNewStack,
      _ref$focusElement = _ref.focusElement,
      focusElement = _ref$focusElement === void 0 ? undefined : _ref$focusElement,
      _ref$dragEvent = _ref.dragEvent,
      dragEvent = _ref$dragEvent === void 0 ? undefined : _ref$dragEvent;
    // attempt to retain focus after dom manipulation, which can break focus
    var maintainFocusElement = document.activeElement;
    var config = _objectSpread({}, configParam);
    if (config.id == null) {
      config.id = nanoid();
    }
    if (dragEvent != null) {
      root === null || root === void 0 || root.layoutManager.createDragSourceFromEvent(config, dragEvent);
      return;
    }
    var searchConfig = replaceConfig || {
      id: config.id,
      component: config.component
    };
    assertNotNull(root);
    var stack = stackParam !== null && stackParam !== void 0 ? stackParam : createNewStack ? LayoutUtils.addStack(root) : LayoutUtils.getStackForRoot(root, searchConfig);
    assertNotNull(stack);
    var oldContentItem = LayoutUtils.getContentItemInStack(stack, searchConfig);
    if (focusElement != null) {
      // We need to listen for when the stack is created
      var onComponentCreated = event => {
        log.debug('Component created, focusing element', focusElement);
        stack.off('componentCreated', onComponentCreated);
        var element = event.origin.element;

        // Need to wait until the component actually renders.
        requestAnimationFrame(() => {
          LayoutUtils.focusElement(element[0], focusElement);
        });
      };
      stack.on('componentCreated', onComponentCreated);
    }
    if (replaceExisting && oldContentItem) {
      var index = stack.contentItems.indexOf(oldContentItem);

      // Using remove/add here instead of replaceChild because I was getting errors with replaceChild... should be the same.
      // Add first so that the stack doesn't get screwed up
      stack.addChild(config, index + 1);
      stack.removeChild(oldContentItem);
      stack.setActiveContentItem(stack.contentItems[index]);
    } else {
      stack.addChild(config);
    }
    if (focusElement == null && maintainFocusElement && isHTMLElement(maintainFocusElement)) {
      maintainFocusElement.focus();
    }
  }

  /**
   * Opens a component in an given stack.
   * If `replaceExisting` is true and there is a component found with the same `config.id`, it will replace that component with this one
   * @param stack The GoldenLayout stack to open the component in
   * @param config The component config definition to open
   * @param replaceExisting Whether it should replace the existing one matching component type and id, or open a new one
   */
  static openComponentInStack(stack, config) {
    var replaceExisting = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var maintainFocusElement = document.activeElement; // attempt to retain focus after dom manipulation, which can break focus

    var searchConfig = {
      id: config.id
    };
    var oldContentItem = LayoutUtils.getContentItemInStack(stack, searchConfig);
    if (replaceExisting && oldContentItem && stack) {
      var index = stack === null || stack === void 0 ? void 0 : stack.contentItems.indexOf(oldContentItem);

      // Using remove/add here instead of replaceChild because I was getting errors with replaceChild... should be the same.
      // Add first so that the stack doesn't get screwed up
      stack.addChild(config, index + 1);
      stack.removeChild(oldContentItem);
      stack.setActiveContentItem(stack.contentItems[index]);
    } else {
      stack === null || stack === void 0 || stack.addChild(config);
    }
    if (maintainFocusElement && isHTMLElement(maintainFocusElement)) {
      maintainFocusElement.focus();
    }
  }

  /**
   * Close the specified component and remove it from the stack it's currently in
   * @param root The GoldenLayout root to search and close the component in
   * @param config The GoldenLayout component config definition to close, eg. { component: 'IrisGridPanel', id: 'table-t' }
   */
  static closeComponent(root, config, closeOptions) {
    var stack = LayoutUtils.getStackForRoot(root, config, false, false, false);
    if (!stack) {
      log.warn('Cannot find stack for component, ignoring close', config);
      return;
    }

    // Find the tab with the specified config and remove it
    // Same component was used to get the stack above, so getContentItemInStack shouldn't return null
    var oldContentItem = LayoutUtils.getContentItemInStack(stack, config);
    var maintainFocusElement = document.activeElement; // attempt to retain focus after dom manipulation, which can break focus
    if (oldContentItem) {
      if (isComponent(oldContentItem)) {
        oldContentItem.container.close(closeOptions);
      } else {
        stack.removeChild(oldContentItem);
      }
    }
    // if focused element is still in dom restore focus, note it could have been in the removed panel so check first
    if (maintainFocusElement && document.contains(maintainFocusElement) && isHTMLElement(maintainFocusElement)) {
      maintainFocusElement.focus();
    }
  }
  static renameComponent(root, config, newTitle) {
    var stack = LayoutUtils.getStackForRoot(root, config, false);
    if (!stack) {
      log.error('Could not find stack for config', config);
      return;
    }
    // Find the tab with the specified config and rename it
    var contentItem = LayoutUtils.getContentItemInStack(stack, config);
    if (contentItem) {
      contentItem.setTitle(newTitle);
    }
  }

  /**
   * Create a component clone based on the given config
   * @param root The GoldenLayout root to clone the component in
   * @param config The config to clone
   * @returns Clone config
   */
  static cloneComponent(root, config) {
    var stack = LayoutUtils.getStackForRoot(root, config, false);
    if (!stack) {
      log.error('Could not find stack for config', config);
      return null;
    }
    var _config$props = config.props,
      props = _config$props === void 0 ? {} : _config$props;
    var panelState = LayoutUtils.getPanelComponentState(config);
    var cloneConfig = {
      type: 'react-component',
      component: config.component,
      props: _objectSpread(_objectSpread({}, props), {}, {
        panelState
      }),
      title: "".concat(config.title, " Copy"),
      id: nanoid()
    };
    LayoutUtils.openComponentInStack(stack, cloneConfig);
    return cloneConfig;
  }

  /**
   * Get panel component state for the given config
   * @param config Panel config
   * @returns Panel state
   */
  static getPanelComponentState(config) {
    if (isComponentConfig(config)) {
      var _config$componentStat;
      return (_config$componentStat = config.componentState) === null || _config$componentStat === void 0 ? void 0 : _config$componentStat.panelState;
    }
    if (isReactComponentConfig(config)) {
      var _config$props2;
      return (_config$props2 = config.props) === null || _config$props2 === void 0 ? void 0 : _config$props2.panelState;
    }
    return null;
  }
  static makeDefaultLayout() {
    return {
      dimensions: {
        headerHeight: GoldenLayoutThemeExport.tabHeight,
        borderWidth: GoldenLayoutThemeExport.dragBorderWidth,
        borderGrabWidth: 10
      },
      settings: {
        showPopoutIcon: false,
        showCloseIcon: false,
        constrainDragToContainer: false
      }
    };
  }

  /**
   * Gets a containers root node
   * @param container The Golden Layout container to get the root for
   */
  static getRootFromContainer(container) {
    return container.layoutManager.root;
  }

  /**
   * Gets the config for the panel component given a glContainer
   * @param container The Golden Layout container to get the config for
   */
  static getComponentConfigFromContainer(container) {
    if (container) {
      if (container.tab != null && container.tab.contentItem != null) {
        return container.tab.contentItem.config;
      }

      // If the container hasn't populated the tab yet, just get the config directly from the container
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore private api usage
      // eslint-disable-next-line no-underscore-dangle
      return container._config;
    }
    return null;
  }
  static getTitleFromContainer(container) {
    if (container != null && container.tab != null && container.tab.contentItem != null) {
      return container.tab.contentItem.config.title;
    }
    return null;
  }
  static getTitleFromTab(tab) {
    if (tab != null && tab.contentItem != null) {
      return tab.contentItem.config.title;
    }
    return null;
  }

  /**
   * Retrieve the panel ID for the provided golden layout container
   * @param glContainer The container to get the panel ID for
   * @returns Panel ID
   */
  static getIdFromContainer(glContainer) {
    var config = LayoutUtils.getComponentConfigFromContainer(glContainer);
    if (config) {
      return config.id;
    }
    return null;
  }

  /**
   * Retrieve the ID of the panel provided
   * @param panel The panel to get the ID for
   * @returns Panel ID
   */
  static getIdFromPanel(panel) {
    var glContainer = panel.props.glContainer;
    return LayoutUtils.getIdFromContainer(glContainer);
  }

  /**
   * Get component name from the panel instance
   * @param panel Panel to get component name for
   * @returns Component name or null if unable to retrieve name
   */
  static getComponentNameFromPanel(panel) {
    var glContainer = panel.props.glContainer;
    var config = LayoutUtils.getComponentConfigFromContainer(glContainer);
    if (config && isReactComponentConfig(config)) {
      var _config$component;
      return (_config$component = config.component) !== null && _config$component !== void 0 ? _config$component : null;
    }
    return null;
  }

  /**
   * Get component name for wrapped and un-wrapped components
   * @param component Component to get name for
   * @returns Component name
   * @throws If displayName for the component is not defined
   */
  static getComponentName(component) {
    var _component$WrappedCom, _component$WrappedCom2;
    var name = (_component$WrappedCom = (_component$WrappedCom2 = component.WrappedComponent) === null || _component$WrappedCom2 === void 0 ? void 0 : _component$WrappedCom2.displayName) !== null && _component$WrappedCom !== void 0 ? _component$WrappedCom : component.displayName;
    if (name == null) {
      throw new Error("Component displayName not defined ".concat(component));
    }
    return name;
  }

  /**
   * Put focus on the first "input" element (input, button, select, textarea) within an element
   * If element is null or input element not found, does nothing
   * @param element The element to put focus in.
   * @param selector The first element matching this selector will be focused.
   * @returns The element that was focused, null if not focused
   */
  static focusElement(element) {
    var selector = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LayoutUtils.DEFAULT_FOCUS_SELECTOR;
    if (element == null) {
      return null;
    }
    var focusElement = element.querySelector(selector);
    if (focusElement == null) {
      return null;
    }
    if (isHTMLElement(focusElement)) {
      focusElement.focus();
    }
    return focusElement;
  }

  /**
   * Get a promise that initializes when layout is initialized
   * @param layout The layout to await initialization on
   * @returns Promise that resolves when layout is initialized
   */
  static onInitialized(layout) {
    return new Promise(resolve => {
      if (layout.isInitialised) {
        resolve();
        return;
      }
      var onInit = () => {
        layout.off('initialised', onInit);
        resolve();
      };
      layout.on('initialised', onInit);
    });
  }
}
_defineProperty(LayoutUtils, "DEFAULT_FOCUS_SELECTOR", 'input, select, textarea, button');
export default LayoutUtils;
//# sourceMappingURL=LayoutUtils.js.map